import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "../lib/useReveal";
import { School, MoveHorizontal, Hammer, Sparkles, AlertTriangle } from "lucide-react";

const gd = (id) => `https://lh3.googleusercontent.com/d/${id}`;

const SCHOOLS = [
  {
    key: "halegere",
    name: "Halegere Village School",
    location: "Karnataka",
    body: "From cracked walls, unsafe roofing and broken sanitation to a fully restored campus — safe classrooms, filtered drinking water and dedicated teachers.",
    heroBefore: gd("1kjSjRa6svQC4bM5y-xpo-RD3Aaa0x0Cn"),
    heroAfter: gd("154nXNlrXgxCiC1r7j_5YdcqWsxQljUwQ"),
    before: [
      "1kjSjRa6svQC4bM5y-xpo-RD3Aaa0x0Cn",
      "12Li46aS7x8JwZOOy5locdbsadOnOp_j_",
      "1ZAP9_znZE9an_Vphf2DlrUl5P8S0K2yp",
      "1jWQOiI8WE_s-_sLwwhugnm1h6cIz053a",
      "1W00pzWE8rHBiB7Rz1x6jai4G-rT-gAJe",
      "1TV-kMvauJh17fGCEu0JigQMWLJidpHNl",
    ].map(gd),
    during: [
      "1erlrArBIBWSJ2gCfjzDdgAJrUDJQ6Z8f",
      "1OMC1q0v3T87JWEgVSG28ELXpJqENesmQ",
      "1ucETKUFOgbQM0ukF6W2tbN4rCllKtSJo",
      "12N3KlWOONE8XPLH0oUi2KICeBgN48PYI",
      "10R-vbqDdnSfPlSfm8x5ock3j6Y7kty6G",
      "1N4cfRBjIGasRZFzhYWtHSMuFi5XoUIcW",
      "1g9QHjIINBYTsR3nitv8ICFWXYHUrnyt5",
      "1mbf3dPZrr8MGLKlmyYSnID-VFFpIHQ45",
    ].map(gd),
    after: [
      "154nXNlrXgxCiC1r7j_5YdcqWsxQljUwQ",
      "1_Gr5EwiFq_UuUAyFNI0Smsl8vPXhge-4",
      "1KuAFlHRwz2PmxHOohmQaHlJPt2UmPgwY",
      "1OlLQsv_bYdEOvaG9WUmf1rwa2U-1Hc80",
    ].map(gd),
  },
  {
    key: "besagara",
    name: "Besagara Halli Village School",
    location: "Kanakapura Taluk, Ramanagara District",
    body: "A full academic and infrastructure rebuild — playground, painted classrooms, hygiene block and long-term teacher assistance so children stay in school.",
    heroBefore: gd("1ZAP9_znZE9an_Vphf2DlrUl5P8S0K2yp"),
    heroAfter: gd("1J15L7epjNdZ780DF6mDGaHZ5w3q7QAfw"),
    before: ["1ZAP9_znZE9an_Vphf2DlrUl5P8S0K2yp", "1W00pzWE8rHBiB7Rz1x6jai4G-rT-gAJe", "1jWQOiI8WE_s-_sLwwhugnm1h6cIz053a"].map(gd),
    during: ["10R-vbqDdnSfPlSfm8x5ock3j6Y7kty6G", "1g9QHjIINBYTsR3nitv8ICFWXYHUrnyt5", "1mbf3dPZrr8MGLKlmyYSnID-VFFpIHQ45"].map(gd),
    after: [
      "1J15L7epjNdZ780DF6mDGaHZ5w3q7QAfw",
      "1_Gr5EwiFq_UuUAyFNI0Smsl8vPXhge-4",
      "1KuAFlHRwz2PmxHOohmQaHlJPt2UmPgwY",
    ].map(gd),
  },
  {
    key: "naganayakanahalli",
    name: "Naganayakanahalli Village School",
    location: "Bengaluru Rural District",
    body: "Modern classrooms with projectors, an ETM digital lab, teacher assistance and safe drinking water — a case study in what the model looks like at maturity.",
    heroBefore: gd("1W00pzWE8rHBiB7Rz1x6jai4G-rT-gAJe"),
    heroAfter: gd("1UCJSWO6mJjnNrAFROaIjsoSucpIiQ-8O"),
    before: ["1W00pzWE8rHBiB7Rz1x6jai4G-rT-gAJe", "1TV-kMvauJh17fGCEu0JigQMWLJidpHNl", "12Li46aS7x8JwZOOy5locdbsadOnOp_j_"].map(gd),
    during: ["1N4cfRBjIGasRZFzhYWtHSMuFi5XoUIcW", "12N3KlWOONE8XPLH0oUi2KICeBgN48PYI", "1ucETKUFOgbQM0ukF6W2tbN4rCllKtSJo"].map(gd),
    after: [
      "1UCJSWO6mJjnNrAFROaIjsoSucpIiQ-8O",
      "1mkBYnLEu7eqkkghg-8jTSf7kX0pUXSEz",
      "1OlLQsv_bYdEOvaG9WUmf1rwa2U-1Hc80",
    ].map(gd),
  },
];

function BeforeAfterSlider({ before, after, alt }) {
  const wrap = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  useEffect(() => {
    const setFromEvent = (clientX) => {
      const el = wrap.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(r.width, clientX - r.left));
      setPos((x / r.width) * 100);
    };
    const onMove = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromEvent(x);
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    const el = wrap.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(r.width, clientX - r.left));
    setPos((x / r.width) * 100);
  };

  return (
    <div
      ref={wrap}
      onMouseDown={onDown}
      onTouchStart={onDown}
      className="relative w-full aspect-[16/10] overflow-hidden bg-navy/10 select-none cursor-ew-resize touch-none"
      data-testid="before-after-slider"
    >
      <img
        src={before}
        alt={`Before — ${alt}`}
        loading="lazy"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute top-4 left-4 bg-coral text-white text-[10px] font-display font-bold uppercase tracking-widest px-3 py-1 rounded-sm z-10">
        Before
      </div>

      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img
          src={after}
          alt={`After — ${alt}`}
          loading="lazy"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
      <div className="absolute top-4 right-4 bg-teal text-white text-[10px] font-display font-bold uppercase tracking-widest px-3 py-1 rounded-sm z-10">
        After
      </div>

      <div className="absolute top-0 bottom-0 z-20" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-[3px] h-full bg-white shadow-[0_0_0_1px_rgba(15,30,61,0.15)]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-navy shadow-lg flex items-center justify-center border-2 border-teal">
          <MoveHorizontal className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function PhaseGrid({ label, note, Icon, tone, images }) {
  const [active, setActive] = useState(null);
  const toneMap = {
    coral: { chip: "bg-coral text-white", ring: "border-coral" },
    sunflower: { chip: "bg-sunflower text-navy", ring: "border-sunflower" },
    teal: { chip: "bg-teal text-white", ring: "border-teal" },
  }[tone];

  return (
    <div>
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest ${toneMap.chip}`}>
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="mt-2 font-display font-extrabold text-navy text-lg md:text-xl">{note}</div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {images.map((src, i) => (
          <button
            key={src + i}
            type="button"
            onClick={() => setActive(src)}
            className={`group relative aspect-[4/3] overflow-hidden bg-navy/10 rounded-sm border-2 ${toneMap.ring}/40 hover:${toneMap.ring} transition-colors focus:outline-none`}
            data-testid={`phase-thumb-${label.replace(/\s+/g, "-").toLowerCase()}-${i}`}
          >
            <img src={src} alt={`${label} ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </button>
        ))}
      </div>

      {active && (
        <div
          onClick={() => setActive(null)}
          className="fixed inset-0 bg-navy/85 z-[100] flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img src={active} alt={label} className="max-h-[92vh] max-w-[92vw] object-contain shadow-2xl" />
        </div>
      )}
    </div>
  );
}

export default function BeforeAfter({ embedded = false }) {
  const ref = useReveal();
  const [tab, setTab] = useState(SCHOOLS[0].key);
  const current = SCHOOLS.find((s) => s.key === tab);

  return (
    <section
      id={embedded ? undefined : "gallery"}
      data-testid="gallery-section"
      className={`relative ${embedded ? "py-8" : "py-16 md:py-32"} bg-cream-soft`}
    >
      <div className={`${embedded ? "max-w-none px-4 sm:px-6 lg:px-8" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"} min-w-0`}>
        {!embedded && (
          <div ref={ref} className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 items-center">
            <div className="max-w-2xl text-center md:text-left">
              <span className="section-chip">Project Visual Records</span>
              <h2 className="mt-4 font-display font-extrabold text-navy text-3xl sm:text-4xl md:text-6xl leading-[1.05] tracking-tight">
                Before &amp; <em>After.</em>
              </h2>
              <p className="mt-4 text-navy/75 font-display text-base md:text-lg leading-relaxed">
                The visible transformation of every adopted school — drag the handle to reveal what community-led
                effort, patience and small monthly gifts can achieve.
              </p>
            </div>
            <Link to="/get-involved#donate" data-testid="gallery-cta" className="btn-vermilion shrink-0">
              Fund the next transformation
            </Link>
          </div>
        )}

        {/* School tabs */}
        <div className="flex flex-wrap gap-2 md:gap-3 mb-8 md:sticky md:top-[132px] z-10 bg-cream-soft/95 backdrop-blur py-3 border-y border-navy/10">
          <span className="text-[10px] font-display font-bold uppercase tracking-widest text-navy/50 self-center mr-1 md:mr-2">Jump to:</span>
          {SCHOOLS.map((s) => (
            <button
              key={s.key}
              data-testid={`gallery-tab-${s.key}`}
              onClick={() => setTab(s.key)}
              className={`px-3 md:px-4 py-2 text-[10px] font-display font-bold uppercase tracking-widest border-2 rounded-sm transition-all ${
                tab === s.key ? "bg-navy text-white border-navy" : "bg-white text-navy border-navy/15 hover:border-navy"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>

        {/* Current school detail */}
        <article className="manuscript-card !p-0 overflow-hidden mb-10">
          <BeforeAfterSlider before={current.heroBefore} after={current.heroAfter} alt={current.name} />
          <div className="p-6 md:p-8">
            <div className="flex items-start gap-3">
              <School className="w-6 h-6 text-teal mt-1 shrink-0" />
              <div>
                <h3 className="font-display font-extrabold text-navy text-2xl md:text-3xl leading-tight">
                  {current.name}
                </h3>
                <div className="mt-1 text-xs uppercase tracking-widest text-teal font-display font-bold">
                  {current.location}
                </div>
              </div>
            </div>
            <p className="mt-4 text-navy/75 font-display text-base md:text-lg leading-relaxed">
              {current.body}
            </p>
          </div>
        </article>

        {/* Three phase grids */}
        <div className="space-y-12">
          <PhaseGrid
            label="The Reality"
            note={`Before Smt. Revathi Kamath's arrival — ${current.name}`}
            Icon={AlertTriangle}
            tone="coral"
            images={current.before}
          />
          <PhaseGrid
            label="The Journey"
            note="School restoration work in progress"
            Icon={Hammer}
            tone="sunflower"
            images={current.during}
          />
          <PhaseGrid
            label="The Dream"
            note="After successful restoration"
            Icon={Sparkles}
            tone="teal"
            images={current.after}
          />
        </div>

        <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-teal/10 border-l-4 border-teal p-5 sm:p-6">
          <div className="min-w-0">
            <div className="font-display font-extrabold text-navy text-lg sm:text-xl md:text-2xl">
              200+ laboratory kits distributed. Countless smiles restored.
            </div>
            <p className="mt-1 text-navy/75 font-display text-sm md:text-base">
              Add your drop — and watch the next school transform.
            </p>
          </div>
          {!embedded && <Link to="/get-involved#donate" className="btn-vermilion shrink-0">Donate now</Link>}
        </div>
      </div>
    </section>
  );
}
